"use client";

import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, deleteZoneCascade, blobToArrayBuffer, compareZoneOrder, type OfflineZone } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";

export function useZones(siteId: string) {
  const zones =
    useLiveQuery(
      async () => {
        const all = await offlineDb.zones.where("siteId").equals(siteId).toArray();
        return all.sort(compareZoneOrder);
      },
      [siteId]
    ) ?? [];

  async function createZone(data: {
    name: string;
    planUrl?: string;
    planType?: string;
    planBlob?: Blob;
  }): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date();

    // Convertir Blob → ArrayBuffer pour stockage Dexie (compatibilité Safari iOS)
    const planBuffer = data.planBlob ? await blobToArrayBuffer(data.planBlob) : undefined;

    // Nouvelle zone ajoutée en fin de liste : order = max existant + 1.
    const existing = await offlineDb.zones.where("siteId").equals(siteId).toArray();
    const nextOrder = existing.reduce((max, z) => Math.max(max, z.order ?? 0), 0) + 1;

    await offlineDb.zones.add({
      id,
      name: data.name,
      planUrl: data.planUrl,
      planType: data.planType ?? "default",
      planBlob: planBuffer,
      planSyncStatus: planBuffer ? "pending" : undefined,
      order: nextOrder,
      siteId,
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending",
    });

    return id;
  }

  /** Réordonne les zones du site selon `orderedIds` (liste complète des ids
   *  dans le nouvel ordre). Réécrit `order` = index, marque pending pour la
   *  sync. Transaction pour rester cohérent même si on coupe le réseau. */
  async function reorderZones(orderedIds: string[]) {
    const now = new Date();
    await offlineDb.transaction("rw", offlineDb.zones, async () => {
      await Promise.all(
        orderedIds.map((id, index) =>
          offlineDb.zones.update(id, {
            order: index,
            updatedAt: now,
            syncStatus: "pending",
          })
        )
      );
    });
  }

  async function updateZone(
    id: string,
    data: Partial<Pick<OfflineZone, "name" | "planUrl" | "planType">> & { planBlob?: Blob }
  ) {
    // Convertir Blob → ArrayBuffer pour stockage Dexie (compatibilité Safari iOS)
    const planBuffer = data.planBlob ? await blobToArrayBuffer(data.planBlob) : undefined;
    const { planBlob: _blob, ...rest } = data;

    await offlineDb.zones.update(id, {
      ...rest,
      ...(planBuffer !== undefined ? { planBlob: planBuffer, planSyncStatus: "pending" } : {}),
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }

  /** Supprime une zone côté serveur puis localement (cascade).
   *  Offline : supprime localement, la sync propagera au serveur. */
  async function deleteZone(id: string) {
    await deleteWithOfflineFallback("zone", id, `/api/zones/${id}`, () =>
      deleteZoneCascade(id)
    );
  }

  return { zones, createZone, updateZone, deleteZone, reorderZones };
}
